import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { authAPI, paymentsAPI } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/shared/Logo";
import { User, Hotel, AlertCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, getStoredUTM } from "@/lib/analytics";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initMode = searchParams.get("mode") || "login";
  const initRole = searchParams.get("role") || "candidate";
  const initPlan = searchParams.get("plan") || null;
  const [mode, setMode] = useState(initMode);
  const [role, setRole] = useState(initRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleRedirecting, setGoogleRedirecting] = useState(false);
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      if (initRole === "hotel" && user.role === "candidate") return;
      const path = user.role === "admin" ? "/admin" : user.role === "hotel" ? "/hotel" : "/candidate";
      navigate(path, { replace: true });
    }
  }, [user, navigate, initRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !privacyAccepted) {
      setError(t("auth.privacy_required"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      let res;
      if (mode === "register") {
        const utm = getStoredUTM();
        res = await authAPI.register({ email, password, name, role, privacy_accepted: true, utm });
        trackEvent("hotel_signup_completed", { role });
      } else {
        res = await authAPI.login({ email, password });
      }
      if (mode === "login" && role !== res.data.role && res.data.role !== "admin" && res.data.role !== "hotel" && res.data.role !== "candidate") {
        const roleLabel = res.data.role === "candidate" ? t("auth.candidate") : t("auth.hotel");
        const targetLabel = role === "hotel" ? t("auth.hotel") : t("auth.candidate");
        setError(t("auth.role_mismatch", { role: roleLabel, target: targetLabel }));
        setLoading(false);
        return;
      }
      setUser(res.data);
      // Store session token in localStorage as fallback for browsers blocking cookies
      if (res.data.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      toast.success(mode === "register" ? t("auth.create_account") : t("auth.login"));
      if (initPlan && res.data.role === "hotel") {
        try {
          const checkout = await paymentsAPI.createCheckout({ plan: initPlan, payment_type: "subscription" });
          window.location.href = checkout.data.url;
          return;
        } catch (e) { console.error("Checkout redirect failed:", e.message); }
      }
      if (mode === "register" && res.data.role === "candidate") {
        sessionStorage.setItem("staffera_welcome", "1");
      }
      // Navigation is handled by the useEffect that watches `user` state.
      // Do NOT call navigate() here — it causes a race condition on mobile
      // where ProtectedRoute sees user=null before React commits the setUser update.
    } catch (err) {
      const msg = err.response?.data?.detail || "Error";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    if (!privacyAccepted && mode === "register") {
      setError(t("auth.privacy_required"));
      return;
    }
    // Show full-screen loading overlay immediately (replaces browser's
    // "connecting to emergentagent.com" message with our branded screen)
    setGoogleRedirecting(true);
    localStorage.setItem("staffera_auth_role", role);
    if (mode === "register") localStorage.setItem("staffera_privacy_accepted", "true");
    if (initPlan) localStorage.setItem("staffera_auth_plan", initPlan);
    // Store the user's current origin so we can redirect back after OAuth
    // (needed when user is on a custom domain like staffera.eu)
    localStorage.setItem("staffera_return_origin", window.location.origin);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use the API base URL for the OAuth callback to avoid custom domain redirects
    // that strip the hash fragment (#session_id=xxx) during intermediate redirects.
    const apiOrigin = process.env.REACT_APP_BACKEND_URL;
    const redirectUrl = apiOrigin + '/auth/callback';
    // Small delay so the loading screen renders before the browser navigates away
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 150);
  };

  // Full-screen loading overlay shown immediately when Google auth is clicked,
  // so the user never sees "connecting to emergentagent.com" from the browser.
  if (googleRedirecting) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="google-redirect-screen">
        <div className="text-center px-6">
          <Logo size="lg" className="text-white justify-center mb-6" />
          <div className="space-y-4">
            <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-white font-body">{t("auth.signing_in")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-4 mb-8">
          <Logo size="lg" className="text-white" />
          <LanguageSelector variant="dark" />
        </div>

        <Card className="bg-white border-slate-200 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-sans text-2xl font-bold text-slate-900">
              {mode === "register" ? t("auth.create_account") : t("auth.login")}
            </CardTitle>
            <CardDescription className="font-body text-slate-500">
              {mode === "register" ? t("auth.subtitle_register") : t("auth.subtitle_login")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Button variant="outline" className="w-full h-11 border-slate-300 hover:bg-slate-50 font-body"
              onClick={handleGoogleAuth} data-testid="google-auth-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t("auth.google")}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-400 font-body">{t("auth.or_email")}</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <>
                  <div>
                    <Label className="font-body text-sm text-slate-600 mb-1.5 block">{t("auth.your_role")}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button type="button" variant={role === "candidate" ? "default" : "outline"}
                        className={`h-11 font-body ${role === "candidate" ? "bg-navy-900 text-white" : "border-slate-300"}`}
                        onClick={() => setRole("candidate")} data-testid="role-candidate-btn">
                        <User className="w-4 h-4 mr-2" /> {t("auth.candidate")}
                      </Button>
                      <Button type="button" variant={role === "hotel" ? "default" : "outline"}
                        className={`h-11 font-body ${role === "hotel" ? "bg-navy-900 text-white" : "border-slate-300"}`}
                        onClick={() => setRole("hotel")} data-testid="role-hotel-btn">
                        <Hotel className="w-4 h-4 mr-2" /> {t("auth.hotel")}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="name" className="font-body text-sm text-slate-600 mb-1.5 block">{t("auth.full_name")}</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 font-body" required data-testid="register-name-input" />
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="email" className="font-body text-sm text-slate-600 mb-1.5 block">{t("auth.email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="h-11 font-body" required data-testid="auth-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="font-body text-sm text-slate-600 mb-1.5 block">{t("auth.password")}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.password_hint")} className="h-11 font-body" required data-testid="auth-password-input" />
              </div>

              {mode === "register" && (
                <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <Checkbox
                    id="privacy"
                    checked={privacyAccepted}
                    onCheckedChange={(v) => { setPrivacyAccepted(!!v); setError(""); }}
                    className="mt-0.5 data-[state=checked]:bg-gold-500 data-[state=checked]:border-gold-500"
                    data-testid="privacy-checkbox"
                  />
                  <label htmlFor="privacy" className="text-sm text-slate-600 font-body leading-snug cursor-pointer">
                    {t("auth.privacy_check")}{" "}
                    <Link to="/privacy" target="_blank" className="text-gold-600 font-semibold hover:underline" data-testid="privacy-link">
                      {t("auth.privacy_link")}
                    </Link>
                  </label>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 font-body" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <Button type="submit" className="w-full h-11 bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm"
                disabled={loading} data-testid="auth-submit-btn">
                {loading ? t("auth.processing") : mode === "register" ? t("auth.submit_register") : t("auth.submit_login")}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 font-body">
              {mode === "register" ? (
                <>{t("auth.have_account")} <button onClick={() => setMode("login")} className="text-gold-600 font-semibold hover:underline" data-testid="switch-to-login">{t("auth.switch_login")}</button></>
              ) : (
                <>{t("auth.no_account")} <button onClick={() => setMode("register")} className="text-gold-600 font-semibold hover:underline" data-testid="switch-to-register">{t("auth.switch_register")}</button></>
              )}
            </p>
            <div className="pt-2 border-t border-slate-100 text-center">
              <Link to="/privacy" target="_blank" className="text-xs text-slate-400 hover:text-gold-600 font-body transition-colors" data-testid="auth-privacy-footer-link">
                <Shield className="w-3 h-3 inline mr-1" />{t("auth.privacy_link")}
              </Link>
              <span className="text-slate-300 mx-2">|</span>
              <Link to="/terms" target="_blank" className="text-xs text-slate-400 hover:text-gold-600 font-body transition-colors" data-testid="auth-terms-footer-link">
                {t("tos.link")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
